import { useEffect, useState } from "react";
import { Bookmark, Coins, BedDouble, Clock, TrendingUp, Inbox, Users, Activity } from "lucide-react";
import { api } from "../lib/api";

const STATUS_STYLES = {
  paid: "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] border-[var(--ed-emerald)]/30",
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
  expired: "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] border-[var(--ed-rule)]",
  inquiry: "bg-blue-50 text-blue-700 border-blue-200",
  initiated: "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] border-[var(--ed-rule)]",
};

function Stat({ Icon, label, value, sub }) {
  return (
    <div className="border border-[var(--ed-rule)] bg-[var(--ed-bg)] rounded-sm p-5 flex items-start gap-4 transition-colors hover:border-[var(--ed-ink)]">
      <div className="w-10 h-10 rounded-full bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] flex items-center justify-center flex-shrink-0">
        <Icon size={17} strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-1.5">{label}</p>
        <p className="font-serif text-[1.7rem] text-[var(--ed-ink)] leading-none">{value}</p>
        {sub && <p className="text-xs text-[var(--ed-ink-muted)] mt-2 leading-snug">{sub}</p>}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-[var(--ed-ink-muted)] py-20 text-center">Loading…</p>;
  if (!data) return <p className="text-[var(--ed-ink-muted)] py-20 text-center">Could not load analytics.</p>;

  const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div data-testid="admin-dashboard-overview">
      <header className="mb-9">
        <p className="ed-overline mb-2">Overview</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Dashboard</h1>
        <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">A live snapshot of bookings, revenue and the lead funnel.</p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Stat Icon={Bookmark}  label="Total Bookings" value={data.totals.bookings} sub={`${data.totals.last_30_days} in the last 30 days`} />
        <Stat Icon={Coins}     label="Revenue"        value={fmt(data.revenue.all_time)} sub={`${fmt(data.revenue.last_30_days)} last 30 days`} />
        <Stat Icon={BedDouble} label="Occupancy"      value={`${data.occupancy.pct}%`} sub={`Next ${data.occupancy.horizon_days} days · ${data.occupancy.published_properties} live residence(s)`} />
        <Stat Icon={Clock}     label="Avg Stay"       value={data.avg_stay_nights} sub="nights per paid booking" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat Icon={TrendingUp} label="Conversion"        value={`${data.conversion_pct}%`} sub="paid / total leads" />
        <Stat Icon={Inbox}      label="Booking Inquiries" value={data.leads.booking_inquiries} />
        <Stat Icon={Users}      label="Contact Messages"  value={data.leads.contact_messages} />
        <Stat Icon={Activity}   label="Owner Inquiries"   value={data.leads.owner_inquiries} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 border border-[var(--ed-rule)] rounded-sm overflow-hidden bg-[var(--ed-bg)]">
          <div className="px-5 py-3.5 border-b border-[var(--ed-rule)] bg-[var(--ed-paper)]/40">
            <h3 className="font-serif text-lg text-[var(--ed-ink)]">Recent Bookings</h3>
          </div>
          <div className="divide-y divide-[var(--ed-rule)]">
            {data.recent_bookings.length === 0 ? (
              <p className="p-6 text-[var(--ed-ink-muted)] text-sm">No bookings yet.</p>
            ) : (
              data.recent_bookings.map((b) => {
                const cls = STATUS_STYLES[b.status] || "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] border-[var(--ed-rule)]";
                return (
                  <div key={b.id} className="px-5 py-3.5 grid grid-cols-12 gap-3 text-sm items-center">
                    <p className="col-span-3 text-[var(--ed-ink)] truncate">{b.full_name}</p>
                    <p className="col-span-4 text-[var(--ed-ink-muted)] text-xs truncate">{b.property_name}</p>
                    <p className="col-span-2 text-[var(--ed-ink-muted)] text-xs">{b.check_in} → {b.check_out}</p>
                    <p className="col-span-2 text-[var(--ed-emerald)] text-sm font-medium">{b.amount ? fmt(b.amount) : "—"}</p>
                    <p className="col-span-1 text-right">
                      <span className={`text-[0.55rem] uppercase tracking-widest px-2 py-1 border ${cls}`}>
                        {(b.status || "").replace(/_/g, " ")}
                      </span>
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="border border-[var(--ed-rule)] rounded-sm overflow-hidden bg-[var(--ed-bg)]">
          <div className="px-5 py-3.5 border-b border-[var(--ed-rule)] bg-[var(--ed-paper)]/40">
            <h3 className="font-serif text-lg text-[var(--ed-ink)]">Most-Booked Residences</h3>
          </div>
          <div className="divide-y divide-[var(--ed-rule)]">
            {data.top_properties.length === 0 ? (
              <p className="p-6 text-[var(--ed-ink-muted)] text-sm">No paid bookings yet.</p>
            ) : (
              data.top_properties.map((p) => (
                <div key={p.name} className="px-5 py-3.5 flex items-center justify-between text-sm">
                  <span className="text-[var(--ed-ink)] truncate pr-3">{p.name}</span>
                  <span className="text-[var(--ed-emerald)] font-serif text-lg">{p.bookings}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
